import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DownlineListPage from './DownlineListPage';
import { useGetDownlineByAdminIdQuery } from '../services/auth/authApi';
import Loading from '../components/Loading';

// User Types Hierarchy
const USER_TYPES = {
  ADMIN: 1,
  SUPERADMIN: 2,
  MINIADMIN: 3,
  MASTER: 4,
  SUPER: 5,
  AGENT: 6,
  CLIENT: 7
};

const DownlineByIdPage = () => {
  const { adminId, parentUserType, parentName } = useParams();
  const navigate = useNavigate();
  const [previousAdminId, setPreviousAdminId] = useState(adminId);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  
  // Reset state when URL changes
  useEffect(() => {
    if (adminId !== previousAdminId) {
      setPreviousAdminId(adminId);
      setCurrentPage(1);
    }
  }, [adminId, previousAdminId]);
  
  // Fetch downline data by adminId
  const { data: downlineData, isLoading, error, isFetching } = useGetDownlineByAdminIdQuery({ 
    adminId, 
    page: currentPage, 
    limit: itemsPerPage 
  });

  if (isLoading || isFetching || adminId !== previousAdminId) {
    return (
      <div className="w-full flex justify-center items-center min-h-[400px]">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-4">
        <div className="bg-white shadow rounded">
          <div className="bg-[#0F2327] px-5 py-3 flex items-center justify-between">
            <span className="text-xl text-white font-normal">
              Downline Users - {decodeURIComponent(parentName || 'User')}
            </span>
            <button
              onClick={() => navigate(-1)}
              className="bg-blue-500 text-white px-4 py-1 rounded hover:bg-blue-600"
            >
              Back
            </button>
          </div>
          <div className="p-4 text-red-500">
            Error loading downline: {error?.data?.message || error.message || 'Failed to load users'}
          </div>
        </div>
      </div>
    );
  }

  // Check if data is an array (normal API response) or object (downline API response)
  let allUsers = [];
  let userTypeString = null;
  
  if (Array.isArray(downlineData?.data)) {
    // Normal response: data is array
    allUsers = downlineData.data;
    // Get userType from response root or first user
    userTypeString = downlineData.userType || downlineData.data[0]?.userType;
  } else if (downlineData?.data && typeof downlineData.data === 'object') {
    // Downline response: data is object with user type keys
    const dataObject = downlineData.data;
    const userTypeKeys = ['MiniAdmin', 'Master', 'Super', 'Agent', 'Client'];
    
    for (const key of userTypeKeys) {
      if (dataObject[key] && Array.isArray(dataObject[key]) && dataObject[key].length > 0) {
        allUsers = [...allUsers, ...dataObject[key]];
        
        // Set userType from first non-empty array's key
        if (!userTypeString) {
          userTypeString = key;
        }
      }
    }
  }
  
  // Mapping from API response userType string to USER_TYPES number
  const typeMapping = {
    'Admin': 'ADMIN',
    'SuperAdmin': 'SUPERADMIN',
    'MiniAdmin': 'MINIADMIN',
    'Master': 'MASTER',
    'Super': 'SUPER',
    'Agent': 'AGENT',
    'Client': 'CLIENT'
  };
  
    const getUserTypeKey = () => {
    if (transformedUsers.length === 0) return 'user';
    
    const userTypeMapping = {
      'SuperAdmin': 'superadmin',
      'MiniAdmin': 'miniadmin',
      'Master': 'master',
      'Super': 'supermaster',
      'Agent': 'agent',
      'Client': 'client'
    };
    
    return userTypeMapping[transformedUsers[0].userType] || 'user';
  };
  // Convert string userType to number using USER_TYPES constant
  const userType = userTypeString ? USER_TYPES[typeMapping[userTypeString]] : null;
  
  const title = `Downline Users - ${decodeURIComponent(parentName || 'User')}`;

  if (!userType || allUsers.length === 0) {
    // Get userType for create link
    const getCreateUserTypeKey = () => {
      // If we have currentUser from API, use its userType to determine child type
      const currentUserType = downlineData?.currentUser?.userType;
      
      if (!userTypeString && !currentUserType) return 'user';
      
      const userTypeMapping = {
        'SuperAdmin': 'miniadmin',
        'MiniAdmin': 'master',
        'Master': 'supermaster',
        'Super': 'agent',
        'Agent': 'client'
      };
      
      // Use currentUser's type if available, otherwise use userTypeString
      const typeToUse = currentUserType || userTypeString;
      return userTypeMapping[typeToUse] || 'user';
    };

    return (
      <div className="w-full p-4">
        <div className="bg-white shadow rounded">
          <div className="bg-[#0F2327] px-5 py-3 flex items-center justify-between">
            <span className="text-xl text-white font-normal">
              {title}
            </span>
            <button
              onClick={() => navigate(-1)}
              className="bg-blue-500 text-white px-4 py-1 rounded hover:bg-blue-600"
            >
              Back
            </button>
          </div>
          <div className="px-3 py-3 flex justify-around gap-4">
            <Link
              to={"/admin-details/create/" + getCreateUserTypeKey()}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              + Create
            </Link>
          </div>
          <div className="p-8 text-center text-gray-500">
            No downline users found
          </div>
        </div>
      </div>
    );
  }

  // Render DownlineListPage with the flattened array
  return (
    <DownlineListPage 
      userType={userType} 
      title={title}
      customData={allUsers}
      isLoading={isFetching}
      key={adminId}
      paginationData={downlineData?.pagination ? {
        currentPage,
        setCurrentPage,
        itemsPerPage,
        setItemsPerPage,
        pagination: {
          totalUsers: downlineData.pagination.totalCount || downlineData.count,
          totalPages: downlineData.pagination.totalPages || Math.ceil(downlineData.count / itemsPerPage),
          currentPage: downlineData.pagination.page || currentPage,
          itemsPerPage: downlineData.pagination.limit || itemsPerPage,
          hasNextPage: downlineData.pagination.hasNextPage,
          hasPrevPage: downlineData.pagination.hasPrevPage
        }
      } : undefined}
    />
  );
};

export default DownlineByIdPage;
